import {
  Image,
  Stack,
  Text,
  Skeleton,
  Box,
  ImageProps,
} from '@chakra-ui/react';
import { Link } from '@chakra-ui/next-js';
import { getStaticMapMarkerUrl } from '~/lib/providers/map';
import { getCloudflareStaticMapUrlForHome } from '~/lib/providers/cloudflare/images';
import { getMapsQueryUrl } from '@habity/utils';
import { getStreet } from '@habity/services';
import { NestedHome } from '@habity/types';
import { ReactNode } from 'react';

const MapWithLink = ({
  home,
  showAddress,
  children,
}: {
  home: NestedHome;
  showAddress: boolean;
  children: ReactNode;
}) => {
  return showAddress ? (
    <Link href={getMapsQueryUrl(home)} target="_blank">
      {children}
    </Link>
  ) : (
    <>{children}</>
  );
};

/**
Generate a CSS style to shift the center of the image
by a _random_ amount in a _random_ direction
Use when you don't want to reveal the exact location of a map image
by leaving it in the center

Scales up the image a bit to give it room to shift
(to avoid leaving gaps around the edges)
*/
function generateCenterObscureStyle(scale = 1.2, offsetPx = 150) {
  const offsetX = (Math.random() - 0.5) * offsetPx;
  const offsetY = (Math.random() - 0.5) * offsetPx;

  return {
    transform: `scale(${scale}) translate(${offsetX}px, ${offsetY}px)`,
  };
}

interface ProfileMapProps {
  home: NestedHome;
  imageProps?: ImageProps;
}

export const ProfileMap = ({ home, imageProps }: ProfileMapProps) => {
  const { status, address } = home || {};
  const { street, category } = address || {};
  const { show_address } = status || {};

  const mapUrl = getCloudflareStaticMapUrlForHome(home);
  const centerObscureStyle = !show_address ? generateCenterObscureStyle() : {};

  return (
    <Stack position="relative" overflow="hidden">
      <MapWithLink home={home} showAddress={show_address}>
        <Image
          width="100%"
          height="500px"
          objectFit="cover"
          src={mapUrl}
          srcSet={`${mapUrl} 2x, ${mapUrl} 1x`}
          fallback={<Skeleton height="500px" />}
          alt={getStreet(home)}
          style={{
            ...centerObscureStyle,
          }}
          {...imageProps}
        />
      </MapWithLink>

      {show_address && (
        <Box>
          <Image
            alt={street || ''}
            title={street || ''}
            height="56px"
            src={getStaticMapMarkerUrl(category)}
            width="41px"
            left="50%"
            position="absolute"
            top="50%"
            transform="translate(-50%, -100%)"
          />
          <Text
            fontSize="sm"
            fontWeight="bold"
            left="50%"
            position="absolute"
            top="56%"
            transform="translate(-50%, -100%)"
            textShadow={`0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff`}
          >
            {street}
          </Text>
        </Box>
      )}
    </Stack>
  );
};
